"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import parse from "html-react-parser";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import { getBlogById } from "@/api";
import BlogComments from "../../components/BlogComments/BlogComments";

const slugify = (text) => {
  return String(text || "")
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

const extractText = (children) => {
  if (!children) return "";
  if (typeof children === "string") return children;
  if (typeof children === "number") return String(children);
  if (Array.isArray(children)) return children.map(extractText).join("");
  if (children.props?.children) return extractText(children.props.children);
  return "";
};

const customSanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    "*": [
      ...(defaultSchema.attributes?.["*"] || []),
      "id",
      "className",
      "data*",
    ],
    a: [
      ...(defaultSchema.attributes?.a || []),
      "href",
      "target",
      "rel",
    ],
    div: [
      ...(defaultSchema.attributes?.div || []),
      ["className", "math", "math-display", "math-inline"]
    ],
    span: [
      ...(defaultSchema.attributes?.span || []),
      ["className", "math", "math-display", "math-inline"]
    ],
    code: [
      ...(defaultSchema.attributes?.code || []),
      ["className", "math-inline", "math-display"]
    ]
  }
};

const normalizeBlogPayload = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  if (payload.blog && typeof payload.blog === "object") return payload.blog;
  if (payload.data && typeof payload.data === "object") return payload.data;
  return payload;
};
const parseArrayLikeString = (value) => {
  if (typeof value !== "string") return null;
  let current = value.trim();
  for (let i = 0; i < 2; i += 1) {
    if (!(current.startsWith("[") && current.endsWith("]"))) break;
    try {
      const parsed = JSON.parse(current);
      if (Array.isArray(parsed)) return parsed;
      if (typeof parsed === "string") {
        current = parsed.trim();
        continue;
      }
    } catch {
      break;
    }
  }
  return null;
};
const toArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).map((item) => String(item));
  if (!value) return [];
  const parsed = parseArrayLikeString(value);
  if (parsed) return parsed.filter(Boolean).map((item) => String(item));
  return [String(value)];
};

const preprocessBlogContent = (content) => {
  if (!content) return "";
  let processed = content.replace(/\\n/g, "\n");
  if (processed.includes("<code>")) {
    processed = processed
      .replace(/<p><code>/gi, "")
      .replace(/<\/code><\/p>/gi, "\n\n")
      .replace(/<code>/gi, "")
      .replace(/<\/code>/gi, "")
      .replace(/<p>/gi, "")
      .replace(/<\/p>/gi, "\n\n")
      .replace(/<br\s*\/?>/gi, "\n\n")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">");
  }
  
  // Collapse excessive paragraph breaks
  processed = processed.replace(/\n{3,}/g, "\n\n");
  
  // Table rows must NOT have blank lines between them in Markdown.
  while (/\|\s*\n\s*\n\s*\|/.test(processed)) {
    processed = processed.replace(/\|\s*\n\s*\n\s*\|/g, "|\n|");
  }
  
  return processed.trim();
};

const BlogDetailClient = ({ blogId }) => {
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [heroImageError, setHeroImageError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchBlog = async () => {
      try {
        setLoading(true);
        setError(null);
        const payload = await getBlogById(blogId);
        const data = normalizeBlogPayload(payload);
        if (!cancelled) {
          setBlog(data);
          setHeroImageError(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Failed to load blog.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    if (blogId) {
      fetchBlog();
    }

    return () => {
      cancelled = true;
    };
  }, [blogId]);

  if (!blogId) {
    return <p className="py-16 text-center text-gray-500">Invalid blog link.</p>;
  }

  if (loading) {
    return (
      <section className="py-24 text-center">
        <p>Loading blog...</p>
      </section>
    );
  }

  if (error || !blog) {
    return (
      <section className="py-24 text-center">
        <p>{error || "Blog not found."}</p>
      </section>
    );
  }

  const createdDateValue = blog.createdAt || blog.created_at || blog.date;
  const parsedDate = createdDateValue ? new Date(createdDateValue) : null;
  const formattedDate =
    parsedDate && !Number.isNaN(parsedDate.getTime())
      ? parsedDate.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : "Date unavailable";

  const categoryList = toArray(blog.category);
  const subCategoryList = toArray(blog.sub_category || blog.subCategory);
  const normalizedCategory = String(categoryList[0] || "").toLowerCase();
  const normalizedSubCategory = String(subCategoryList[0] || "").toLowerCase();
  const categoryLabel = categoryList.length ? categoryList.join(", ") : "Uncategorized";
  const categoryLink = `/blog?category=${encodeURIComponent(normalizedCategory)}`;
  const subCategoryLabel = subCategoryList.join(", ");
  const subCategoryLink =
    normalizedCategory && normalizedSubCategory
      ? `/blog?category=${encodeURIComponent(
          normalizedCategory
        )}&sub_category=${encodeURIComponent(normalizedSubCategory)}`
      : "";
  const heroImage = blog.cover_image || "";

  return (
    <section className="w-full py-[56px] lg:py-[80px] px-[16px] lg:px-[80px]">
      <div className="max-w-4xl mx-auto">
        <article className="w-full">
          <div className="blog-header mb-6 lg:mb-8">
            <nav className="flex flex-wrap items-center gap-2 text-sm text-gray-500 mb-4">
              <Link href="/blog" className="hover:text-primary transition-colors">
                Blogs
              </Link>
              <span>{">"}</span>
              <Link
                href={categoryLink}
                className="hover:text-primary transition-colors uppercase tracking-wide"
              >
                {categoryLabel}
              </Link>
              {subCategoryLabel ? (
                <>
                  <span>{">"}</span>
                  <Link
                    href={subCategoryLink || "/blog"}
                    className="hover:text-primary transition-colors uppercase tracking-wide"
                  >
                    {subCategoryLabel}
                  </Link>
                </>
              ) : null}
              <span>{">"}</span>
              <span className="text-gray-700">{blog.title}</span>
            </nav>
            <h1 className="blog-title text-[34px] lg:text-[48px] leading-tight mb-4">
              {blog.title}
            </h1>
            {blog.short_description ? (
              <p className="text-[17px] leading-7 text-gray-600 mb-4">
                {blog.short_description}
              </p>
            ) : null}
            <div className="blog-meta flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
              <span>By {blog.author || "SOFAR Team"}</span>
              <span>{formattedDate}</span>
            </div>
          </div>

          {heroImage ? (
            <div className="relative w-full h-[240px] md:h-[320px] lg:h-[420px] mb-10 rounded-xl overflow-hidden">
              {!heroImageError ? (
                <img
                  src={heroImage}
                  alt={blog.title}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                  onError={() => setHeroImageError(true)}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-red-50 text-red-700 text-sm px-4 text-center">
                  Cover image failed to load. The image URL may be broken or
                  blocked.
                </div>
              )}
            </div>
          ) : (
            <div className="w-full rounded-xl bg-red-50 text-red-700 text-sm px-4 py-3 mb-10">
              No cover image is configured for this blog.
            </div>
          )}

          <div className="blog-content prose lg:prose-xl max-w-none mx-auto mb-14">
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkMath]}
              rehypePlugins={[
                rehypeRaw,
                [rehypeSanitize, customSanitizeSchema],
                rehypeKatex
              ]}
              components={{
                h1: ({ node, children, ...props }) => {
                  const id = slugify(extractText(children));
                  return (
                    <h1 id={id} className="scroll-mt-24" {...props}>
                      {children}
                    </h1>
                  );
                },
                h2: ({ node, children, ...props }) => {
                  const id = slugify(extractText(children));
                  return (
                    <h2 id={id} className="scroll-mt-24" {...props}>
                      {children}
                    </h2>
                  );
                },
                h3: ({ node, children, ...props }) => {
                  const id = slugify(extractText(children));
                  return (
                    <h3 id={id} className="scroll-mt-24" {...props}>
                      {children}
                    </h3>
                  );
                },
                h4: ({ node, children, ...props }) => {
                  const id = slugify(extractText(children));
                  return (
                    <h4 id={id} className="scroll-mt-24" {...props}>
                      {children}
                    </h4>
                  );
                },
                h5: ({ node, children, ...props }) => {
                  const id = slugify(extractText(children));
                  return (
                    <h5 id={id} className="scroll-mt-24" {...props}>
                      {children}
                    </h5>
                  );
                },
                h6: ({ node, children, ...props }) => {
                  const id = slugify(extractText(children));
                  return (
                    <h6 id={id} className="scroll-mt-24" {...props}>
                      {children}
                    </h6>
                  );
                },
                a: ({ node, href, children, ...props }) => {
                  if (href && href.startsWith("#")) {
                    return (
                      <a
                        href={href}
                        onClick={(e) => {
                          e.preventDefault();
                          const targetId = href.replace(/^#/, "");
                          const target =
                            document.getElementById(targetId) ||
                            document.getElementById(decodeURIComponent(targetId)) ||
                            document.getElementById(slugify(targetId));
                          if (target) {
                            target.scrollIntoView({
                              behavior: "smooth",
                              block: "start",
                            });
                            window.history.pushState(null, "", href);
                          }
                        }}
                        className="text-primary hover:underline cursor-pointer"
                        {...props}
                      >
                        {children}
                      </a>
                    );
                  }
                  return (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                      {...props}
                    >
                      {children}
                    </a>
                  );
                },
              }}
            >
              {preprocessBlogContent(blog.content)}
            </ReactMarkdown>
          </div>

          <BlogComments initialComments={blog.comments || []} blogId={blogId} />
        </article>
      </div>
    </section>
  );
};

export default BlogDetailClient;
